/**
 * Cross-tab signal for "this provider has a video-call tab open", read by MyDayToday to
 * suppress the "new patient available" notification. eventVc opens in a separate tab and
 * localStorage is shared same-origin. Each call tab registers its own id+timestamp, so the
 * flag stays on until the last tab closes. Stale entries (past STALE_AFTER_MS, e.g. a
 * crashed tab) are ignored on read — failing toward firing rather than permanent silence.
 */

export const PROVIDER_IN_VIDEO_CALL = 'PROVIDER_IN_VIDEO_CALL';

const STALE_AFTER_MS = 60_000;

type CallTabEntries = Record<string, number>;

function readEntries(): CallTabEntries {
  try {
    const raw = localStorage.getItem(PROVIDER_IN_VIDEO_CALL);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as CallTabEntries;
    }
    return {};
  } catch {
    return {};
  }
}

function freshEntries(entries: CallTabEntries): CallTabEntries {
  const cutoff = Date.now() - STALE_AFTER_MS;
  return Object.fromEntries(Object.entries(entries).filter(([, ts]) => ts >= cutoff));
}

function writeEntries(entries: CallTabEntries): void {
  // setItem/removeItem can throw (quota exceeded, storage disabled, Safari private mode).
  // Fail open: a failed write must never break the video-call page on mount/unmount.
  try {
    if (Object.keys(entries).length === 0) {
      localStorage.removeItem(PROVIDER_IN_VIDEO_CALL);
      return;
    }
    localStorage.setItem(PROVIDER_IN_VIDEO_CALL, JSON.stringify(entries));
  } catch {
    // ignore — read side already treats absent/unreadable storage as "not in call"
  }
}

/** Register (or refresh) this tab as an open video call. */
export function markProviderInVideoCall(tabId: string): void {
  const entries = freshEntries(readEntries());
  entries[tabId] = Date.now();
  writeEntries(entries);
}

/** Deregister this tab; the flag clears only once no fresh call tab remains. */
export function unmarkProviderInVideoCall(tabId: string): void {
  const entries = freshEntries(readEntries());
  delete entries[tabId];
  writeEntries(entries);
}

/** True while at least one non-stale video call tab is open. */
export function isProviderInVideoCall(): boolean {
  return Object.keys(freshEntries(readEntries())).length > 0;
}
